import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { CalendarClock, ExternalLink, Upload as UploadIcon } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ChannelIcon } from "@/lib/channel-icons"
import { channelColor } from "@/lib/channel-colors"
import { useFormatDateTime } from "@/lib/datetime"
import { isSchedulePastDue } from "@/lib/scheduling"
import { PastDueWarning } from "@/components/scheduling/PastDueWarning"
import { AttachmentViewer } from "@/components/attachments/AttachmentViewer"
import { TagBadge } from "@/components/tags/TagBadge"
import { cn } from "@/lib/utils"

// Shape returned by GET /pieces/:id/preview.
type FieldSchema = {
  name: string
  label: string
  kind: "text" | "markdown" | "boolean" | "slug" | "images" | "video" | "featured_image"
  required: boolean
  multiline?: boolean
  options?: string[]
}

type PreviewAttachment = {
  id: number
  kind: string
  filename: string | null
  content_type: string | null
  url: string | null
  position: number
}

type PreviewPayload = {
  piece: {
    id: number
    name: string
    channel: string
    display_title: string | null
    fields: Record<string, unknown>
    publishing_status: string
    published_url: string | null
    published_at: string | null
    slot: {
      publish_at: string
      upload_at: string | null
      use_platform_scheduling: boolean
      manual_publish: boolean
    } | null
    tags: Array<{ id: number; name: string; color: string; icon: string }>
    attachments: PreviewAttachment[]
  }
  channel: {
    slug: string
    name: string
    channel_type: string
    color: string
    character_limit: number | null
    fields_schema: FieldSchema[]
  }
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Draft",
  queued_to_schedule: "Queued to schedule",
  queued_to_publish: "Queued to publish",
  scheduled: "Scheduled",
  published: "Published",
  failed: "Failed",
  backburner: "Backburner",
  archived: "Archived",
}

// Context lets any CalendarPieceCard (week/month/list) request the preview
// without threading a callback through every view. Null when no provider is
// mounted — cards then fall back to plain navigation.
const PiecePreviewContext = createContext<((pieceId: number) => void) | null>(null)

export function usePiecePreview() {
  return useContext(PiecePreviewContext)
}

// Wraps the calendar views, owns the "which piece is open" state, and renders
// the modal. Mount once around the calendar grid/list.
export function PiecePreviewProvider({ children }: { children: ReactNode }) {
  const [openPieceId, setOpenPieceId] = useState<number | null>(null)

  return (
    <PiecePreviewContext.Provider value={setOpenPieceId}>
      {children}
      <PiecePreviewModal pieceId={openPieceId} onClose={() => setOpenPieceId(null)} />
    </PiecePreviewContext.Provider>
  )
}

function PiecePreviewModal({ pieceId, onClose }: { pieceId: number | null; onClose: () => void }) {
  const [data, setData] = useState<PreviewPayload | null>(null)
  const [error, setError] = useState(false)

  // Fetch (or refetch) whenever the opened piece changes. An AbortController
  // drops a stale response if the user opens another piece mid-flight.
  useEffect(() => {
    if (pieceId == null) return
    setData(null)
    setError(false)
    const controller = new AbortController()
    fetch(`/pieces/${pieceId}/preview`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((json: PreviewPayload) => setData(json))
      .catch((e) => {
        if (e.name !== "AbortError") setError(true)
      })
    return () => controller.abort()
  }, [pieceId])

  return (
    <Dialog open={pieceId != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg" className={data ? channelColor(data.channel.color).theme : undefined}>
        {error ? (
          <>
            <DialogHeader>
              <DialogTitle>Couldn’t load this piece</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-muted">
              Something went wrong fetching the piece. Try opening its full view.
            </p>
            <PreviewFooter pieceId={pieceId} onClose={onClose} />
          </>
        ) : !data ? (
          <>
            <DialogHeader>
              <DialogTitle>Loading…</DialogTitle>
            </DialogHeader>
            <div className="h-24 animate-pulse rounded-md bg-surface" />
          </>
        ) : (
          <PreviewBody data={data} pieceId={pieceId} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PreviewBody({
  data,
  pieceId,
  onClose,
}: {
  data: PreviewPayload
  pieceId: number | null
  onClose: () => void
}) {
  const { piece, channel } = data
  const fmt = useFormatDateTime()
  const color = channelColor(channel.color)
  const title = piece.display_title || piece.name
  const statusLabel = STATUS_LABELS[piece.publishing_status] ?? piece.publishing_status

  return (
    <>
      <DialogHeader>
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          <ChannelIcon channelType={channel.channel_type} color={channel.color} className="h-4 w-4" />
          <span>{channel.name}</span>
          {piece.tags.map((tag) => (
            <TagBadge key={tag.id} tag={tag} className="px-1.5 py-0.5 text-[10px]" />
          ))}
        </div>
        <DialogTitle className={cn("pr-8", color.text)}>{title}</DialogTitle>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{statusLabel}</Badge>
          <ScheduleLine piece={piece} fmt={fmt} />
        </div>
      </DialogHeader>

      <div className="space-y-5">
        {channel.fields_schema.map((field) => (
          <PreviewField key={field.name} field={field} piece={piece} schema={channel.fields_schema} />
        ))}
      </div>

      <PreviewFooter pieceId={pieceId} onClose={onClose} />
    </>
  )
}

// Compact upload/publish (or published) line under the title.
function ScheduleLine({
  piece,
  fmt,
}: {
  piece: PreviewPayload["piece"]
  fmt: ReturnType<typeof useFormatDateTime>
}) {
  if (piece.publishing_status === "published" && piece.published_at) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
        <CalendarClock className="h-3.5 w-3.5" />
        Published {fmt(piece.published_at)}
      </span>
    )
  }
  if (!piece.slot) return null
  const pastDue = isSchedulePastDue(piece.publishing_status, piece.slot.publish_at)
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-muted">
      {piece.slot.use_platform_scheduling && piece.slot.upload_at && (
        <span className="inline-flex items-center gap-1.5">
          <UploadIcon className="h-3.5 w-3.5" />
          Upload {fmt(piece.slot.upload_at)}
        </span>
      )}
      <span className="inline-flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5" />
        {piece.slot.use_platform_scheduling ? "Publish" : "Scheduled"} {fmt(piece.slot.publish_at)}
        {piece.slot.manual_publish && " (manual)"}
      </span>
      {pastDue && <PastDueWarning message="Past due" className="text-xs" />}
    </span>
  )
}

// One read-only field block, dispatching on the schema kind. Mirrors the
// editor's FieldRenderer but for display only.
function PreviewField({
  field,
  piece,
  schema,
}: {
  field: FieldSchema
  piece: PreviewPayload["piece"]
  schema: FieldSchema[]
}) {
  const label = <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{field.label}</p>

  if (field.kind === "featured_image") {
    const current = piece.attachments.find((a) => a.kind === "featured_image")
    return (
      <div className="space-y-1.5">
        {label}
        {current?.url ? (
          <img src={current.url} alt={current.filename ?? ""} className="max-h-48 rounded-md border border-hairline object-contain" />
        ) : (
          <Empty />
        )}
      </div>
    )
  }

  if (field.kind === "images" || field.kind === "video") {
    // Render the media grid once, on the schema's first attachment field.
    const firstAttachmentField = schema.find((f) => f.kind === "images" || f.kind === "video")
    if (firstAttachmentField?.name !== field.name) return null
    const items = piece.attachments.filter((a) => a.kind === "image" || a.kind === "video")
    return (
      <div className="space-y-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Media</p>
        {items.length === 0 ? (
          <Empty />
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((a) =>
              a.kind === "image" && a.url ? (
                <img key={a.id} src={a.url} alt={a.filename ?? ""} className="h-24 w-24 rounded-md border border-hairline object-cover" />
              ) : (
                <div key={a.id} className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-md border border-hairline bg-surface p-2 text-center">
                  <span className="truncate text-[10px] text-ink-muted" title={a.filename ?? ""}>
                    {a.filename ?? "Video"}
                  </span>
                  <AttachmentViewer url={a.url} filename={a.filename} kind="video" />
                </div>
              ),
            )}
          </div>
        )}
      </div>
    )
  }

  const value = piece.fields[field.name]

  if (field.kind === "boolean") {
    return (
      <div className="space-y-1.5">
        {label}
        <p className="text-sm text-ink-body">{value === true ? "Yes" : "No"}</p>
      </div>
    )
  }

  const text = typeof value === "string" ? value : value == null ? "" : String(value)
  const isLong = field.kind === "markdown" || field.multiline

  return (
    <div className="space-y-1.5">
      {label}
      {text.trim().length === 0 ? (
        <Empty />
      ) : (
        <p className={cn("text-sm text-ink-body", isLong && "whitespace-pre-wrap")}>{text}</p>
      )}
    </div>
  )
}

function Empty() {
  return <p className="text-sm italic text-ink-muted">Not set</p>
}

function PreviewFooter({ pieceId, onClose }: { pieceId: number | null; onClose: () => void }) {
  return (
    <DialogFooter>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {pieceId != null && (
        <Button asChild>
          <Link href={`/pieces/${pieceId}`}>
            <ExternalLink className="h-4 w-4" />
            Open full view
          </Link>
        </Button>
      )}
    </DialogFooter>
  )
}
